import { FC, memo, useEffect, useState } from 'react';
import { Input, Button, Space, message } from 'antd';
import { useAtom, useSetAtom } from 'jotai';
import { editingMapDataAtom, mapDataListRefreshTrigger, showMapDataUpdatePanelAtom } from '../../stores/mapStore';
import FormItemWrapper from '~/components/common/FormItemWrapper/FormItemWrapper';
import ProjectionSelector from '~/components/app_common/ProjectionSelector/ProjectionSelector';
import { MapData_InputMetadata, MapData_ProcessStatus, MapData_Update_Request } from '~/common/typescript/models/MapData';
import { MapDataAPIs } from '~/common/typescript/apis/MapDataAPIs';
import styled from 'styled-components';
import { UI_Header, UI_HeaderLeft, UI_HeaderRight, UI_HeaderRight_item } from '../PanelStyle';
import { FaXmark } from 'react-icons/fa6';
import { PiPencilSimpleBold } from 'react-icons/pi';
import { Rnd } from 'react-rnd';
import MapData_UploadFile from './MapData_UploadFile';

const MapData_UpdatePanel: FC = () => {
  const [isOpen, setIsOpen] = useAtom(showMapDataUpdatePanelAtom);
  const [mapData, setMapData] = useAtom(editingMapDataAtom);
  const setRefreshTrigger = useSetAtom(mapDataListRefreshTrigger);

  const [name, setName] = useState('');
  const [srs, setSrs] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mapData) {
      setName(mapData.name || '');
      try {
        const metadata: MapData_InputMetadata = JSON.parse(mapData.processInputMetadata || '{}');
        setSrs(metadata.srs);
      } catch (e) {
        console.error("Failed to parse metadata", e);
        setSrs(undefined);
      }
    } else {
      setName('');
      setSrs(undefined);
    }
  }, [mapData]);

  const handleClose = () => {
    setIsOpen(false);
    setMapData(null);
  };

  const performUpdate = async (payload: MapData_Update_Request) => {
    if (!mapData?.id) return;

    setLoading(true);
    try {
      await MapDataAPIs.remote_mapData_update(mapData.id, payload);
      message.success('Cập nhật thành công!');
      setRefreshTrigger(v => v + 1);
      handleClose();
    } catch (error) {
      console.error('Update failed', error);
      message.error('Cập nhật thất bại!');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    performUpdate({ name });
  };

  const handleProcess = () => {
    const payload: MapData_Update_Request = {
      processStatus: MapData_ProcessStatus.SUBMITED,
    };
    performUpdate(payload);
  };

  const handleSaveAndProcess = () => {
    const metadata: MapData_InputMetadata = { srs };
    const payload: MapData_Update_Request = {
      name: name,
      processInputMetadata: JSON.stringify(metadata),
      processStatus: MapData_ProcessStatus.SUBMITED,
    };
    performUpdate(payload);
  };

  if (!isOpen || !mapData) {
    return null;
  }

  return (
    <Rnd
      default={{
        x: window.innerWidth / 2 - 300,
        y: window.innerHeight / 2 - 250,
        width: 600,
        height: 500,
      }}
      dragHandleClassName="draggable-header"
      minWidth={400}
      minHeight={300}
      style={{ zIndex: 1000 }}
      bounds="window"
    >
      <UI_PanelWrapper>
        <UI_Header className="draggable-header" style={{ cursor: 'move' }}>
          <UI_HeaderLeft style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <PiPencilSimpleBold style={{ color: '#2563eb', fontSize: '18px' }} />
            <span>{`Cập nhật dữ liệu: ${mapData?.name}`}</span>
          </UI_HeaderLeft>
          <UI_HeaderRight>
            <UI_HeaderRight_item onClick={handleClose}>
              <FaXmark />
            </UI_HeaderRight_item>
          </UI_HeaderRight>
        </UI_Header>
        <UI_Content>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <FormItemWrapper label="Tên">
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </FormItemWrapper>


            <FormItemWrapper label="Hệ chiếu (SRS)" labelWidth={300}>
              <ProjectionSelector
                defaultValue={srs}
                onChange={setSrs}
              />
            </FormItemWrapper>

            <MapData_UploadFile mapData={mapData} />
          </Space>
        </UI_Content>
        <UI_Footer>
          <Space>
            <Button key="save" type="default" onClick={handleSave} loading={loading}>
              Lưu
            </Button>
            <Button key="save_process" type="primary" onClick={handleSaveAndProcess} loading={loading}>
              Lưu và Xử lý
            </Button>
          </Space>
        </UI_Footer>
      </UI_PanelWrapper>
    </Rnd>
  );
};

export default memo(MapData_UpdatePanel);


const UI_PanelWrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  box-shadow: var(--shadows-paneShadow);
  z-index: 999999; /* Increased z-index */
  display: flex;
  flex-direction: column;
  border: 1px solid #ccc;
`;

const UI_Content = styled.div`
  padding: 16px;
  flex-grow: 1;
  overflow-y: auto;
`;

const UI_Footer = styled.div`
  padding: 10px 16px;
  text-align: right;
  border-top: 1px solid #f0f0f0;
`;